import { useEffect, useState, useMemo } from "react";
import api, { openPdf } from "../lib/api";
import FilterPills, { useQueryFilter } from "../components/FilterPills";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, FileText, Ban, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Stornoprotokoll — Buchhalterisches Journal aller Storno- und
 * Gutschrift-Belege. Zeigt Original-Rechnung, Grund, Betrag (negativ) und
 * ob Ware zurückgebucht wurde. Für Steuerberater + Revision.
 */
const REASON_COLORS = {
  falschlieferung: "#dc2626",
  reklamation: "#ea580c",
  preisminderung: "#7c3aed",
  retoure: "#0891b2",
  kundenwiderruf: "#6b7280",
  sonstiges: "#525252",
};

export default function Stornos() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useQueryFilter("typ", "all"); // all | storno | credit_note
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/storno-journal");
      setRows(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Journal konnte nicht geladen werden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fmt = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter !== "all" && r.type !== filter) return false;
      if (!q) return true;
      const hay = `${r.invoice_number} ${r.storno_of_number} ${r.customer_name} ${r.reason_label} ${r.reason_note || ""}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [rows, q, filter]);

  const totals = useMemo(() => {
    let sumStorno = 0, sumCredit = 0, restocked = 0;
    for (const r of filtered) {
      if (r.type === "storno") sumStorno += r.gross;
      else if (r.type === "credit_note") sumCredit += r.gross;
      if (r.restocked) restocked += 1;
    }
    return { sumStorno, sumCredit, restocked };
  }, [filtered]);

  return (
    <div data-testid="page-stornos">
      <PageHeader
        eyebrow="Buchhaltung"
        title="Stornoprotokoll"
        description="Alle Storno- und Gutschrift-Belege chronologisch — mit Grund, Original-Rechnung, Nettosumme und Lager-Status. GoBD-konform."
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-red-200 rounded-2xl p-5" data-testid="kpi-storno-sum">
          <div className="overline text-red-700 inline-flex items-center gap-1"><Ban size={12} /> Storno-Summe</div>
          <div className="font-display font-black text-3xl mt-1 text-red-700">{fmt(totals.sumStorno)}</div>
        </div>
        <div className="bg-white border border-purple-200 rounded-2xl p-5" data-testid="kpi-credit-sum">
          <div className="overline text-purple-700 inline-flex items-center gap-1"><RotateCcw size={12} /> Gutschrift-Summe</div>
          <div className="font-display font-black text-3xl mt-1 text-purple-700">{fmt(totals.sumCredit)}</div>
        </div>
        <div className="bg-white border border-green-200 rounded-2xl p-5" data-testid="kpi-restocked">
          <div className="overline text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Zurückgebucht</div>
          <div className="font-display font-black text-3xl mt-1 text-green-700">
            {totals.restocked} <span className="text-base text-[var(--text-muted)] font-normal">/ {filtered.length}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="p-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input placeholder="RE-Nr., Kunde, Grund…" value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-8 rounded-xl h-9" data-testid="input-storno-search" />
          </div>
          <FilterPills
            options={[["all", "Alle", "#e5e5e5"], ["storno", "Storno", "#fecaca"], ["credit_note", "Gutschrift", "#e9d5ff"]]}
            value={filter}
            onChange={setFilter}
            testidPrefix="filter-storno-"
          />
          <div className="text-xs text-[var(--text-muted)] ml-auto">{filtered.length} Belege</div>
        </div>

        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Typ</TableHead>
              <TableHead className="overline">Beleg</TableHead>
              <TableHead className="overline">Original</TableHead>
              <TableHead className="overline">Kunde</TableHead>
              <TableHead className="overline">Datum</TableHead>
              <TableHead className="overline">Grund</TableHead>
              <TableHead className="overline text-right">Netto</TableHead>
              <TableHead className="overline text-right">Brutto</TableHead>
              <TableHead className="overline text-center">Lager</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => {
              const c = REASON_COLORS[r.reason_code] || "#9ca3af";
              return (
                <TableRow key={r.id} data-testid={`storno-row-${r.invoice_number}`}>
                  <TableCell>
                    {r.type === "storno"
                      ? <span className="pop-pill bg-red-100 text-red-700 text-[10px] font-bold"><Ban size={10} className="inline mr-0.5" /> STORNO</span>
                      : <span className="pop-pill bg-purple-100 text-purple-700 text-[10px] font-bold"><RotateCcw size={10} className="inline mr-0.5" /> GUTSCHRIFT</span>}
                  </TableCell>
                  <TableCell className="font-mono font-bold">{r.invoice_number}</TableCell>
                  <TableCell className="font-mono text-xs text-[var(--text-muted)]">{r.storno_of_number}</TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="pop-pill text-[10px] font-bold w-fit"
                            style={{ background: c + "20", color: c }}
                            data-testid={`reason-${r.invoice_number}`}>
                        {r.reason_label}
                      </span>
                      {r.reason_note && (
                        <span className="text-[10px] text-[var(--text-muted)] mt-0.5 max-w-[240px] truncate"
                              title={r.reason_note}>
                          {r.reason_note}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-red-700 font-semibold">
                    {fmt(r.net)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-red-700 font-bold">
                    {fmt(r.gross)}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.restocked
                      ? <span className="pop-pill bg-green-100 text-green-800 text-[10px] font-bold" data-testid={`restocked-${r.invoice_number}`}>
                          ✓ zurück
                        </span>
                      : <span className="text-[10px] text-[var(--text-muted)]">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <button data-testid={`btn-storno-pdf-${r.invoice_number}`}
                            onClick={() => openPdf(`/delivery-notes/${r.id}/invoice.pdf`).catch(() => toast.error("PDF Fehler"))}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline">
                      <FileText size={14} /> PDF
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-10 text-[var(--text-muted)]">
                  {loading ? "Lade…" : "Keine Storno-/Gutschrift-Belege vorhanden."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
